import {
	Avatar,
	DropdownList,
	DropdownListItem,
	DropdownWithButton,
	icon,
	Icon,
	LabelRegular,
	MultilineEllipsis,
	Pill,
	TextMedium,
	TextRegular,
} from "@hm/fundamental"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CommentDeleteModal } from "./comment-delete-modal"
import { CommentEditForm } from "./comment-edit-form"
import type { BaseCommentAttachment, BaseControlledComment } from "./types"

interface CommentProps {
	comment: BaseControlledComment
	onEdit: (content: string) => Promise<void>
	onDelete: () => Promise<void>
	onDownloadAttachment?: (
		commentId: BaseControlledComment["id"],
		attachment: BaseCommentAttachment,
	) => void
}

export const Comment: React.FC<CommentProps> = ({
	comment,
	onEdit,
	onDelete,
	onDownloadAttachment,
}) => {
	const {
		id,
		avatarUrl,
		name,
		date,
		content,
		pill,
		attachments,
		isEditable = true,
		isDeletable = true,
	} = comment

	const { t } = useTranslation("shared", { keyPrefix: "commentBox" })

	const [isEditing, setIsEditing] = useState(false)
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)
	const [hasEllipsis, setHasEllipsis] = useState(false)

	const handleConfirmDelete = async () => {
		setIsDeleting(true)
		try {
			await onDelete()
			setIsDeleting(false)
			setIsDeleteOpen(false)
		} catch {
			setIsDeleting(false)
		}
	}

	return (
		<div
			data-cy={`comment-${id}`}
			className="grid grid-cols-[24px_1fr] gap-x-2 relative"
		>
			<div className="self-center">
				{avatarUrl ? (
					<img
						src={avatarUrl}
						alt=""
						className="w-full rounded-full"
					/>
				) : (
					<Avatar name={name} className="w-full rounded-full" />
				)}
			</div>
			<div className="flex justify-between text-foreground">
				<div className="flex gap-x-[6px] items-center">
					<TextMedium>{name}</TextMedium>
					<span className="text-foreground_faded">•</span>
					<LabelRegular className="text-foreground_faded">
						{date}
					</LabelRegular>
				</div>
				<div className="flex gap-x-2 items-center">
					{pill ? (
						<Pill
							text={pill.text}
							type={pill.type}
							labelIcon={pill.icon}
							variant="shade"
							shape="rounded"
						/>
					) : (
						(isEditable || isDeletable) && (
							<DropdownWithButton
								data-cy="comment-actions"
								align="right"
								label=""
								buttonProps={{
									square: true,
									variant: "tertiary",
									size: "small",
									icon: icon.mdiDotsHorizontal,
								}}
								dropdownWidth={125}
							>
								<DropdownList>
									{isEditable && (
										<DropdownListItem
											data-cy="comment-edit-action"
											icon={icon.mdiPencilOutline}
											title={t("dropdownActions.edit")}
											value="edit"
											onClick={() => setIsEditing(true)}
										/>
									)}
									{isDeletable && (
										<DropdownListItem
											data-cy="comment-delete-action"
											icon={icon.mdiDelete}
											iconClassName="text-negative"
											title={t("dropdownActions.delete")}
											value="delete"
											onClick={() =>
												setIsDeleteOpen(true)
											}
										/>
									)}
								</DropdownList>
							</DropdownWithButton>
						)
					)}
				</div>
			</div>
			<div />
			<div className="text-foreground pt-1">
				{isEditing ? (
					<CommentEditForm
						content={content}
						onSave={async newContent => {
							await onEdit(newContent)
							setIsEditing(false)
						}}
						onCancel={() => setIsEditing(false)}
					/>
				) : (
					<>
						<MultilineEllipsis
							lines={isExpanded ? null : 2}
							onEllipsisShow={() => setHasEllipsis(true)}
							onEllipsisHidden={() => setHasEllipsis(false)}
						>
							<TextRegular>{content}</TextRegular>
						</MultilineEllipsis>
						{hasEllipsis && !isExpanded && (
							<LabelRegular
								data-cy="comment-read-more"
								className="text-foreground_interaction hover:text-foreground_interaction_hover cursor-pointer"
								onClick={() => setIsExpanded(true)}
							>
								{t("readMore")}
							</LabelRegular>
						)}
						{!!attachments?.length && (
							<div
								data-cy={`comment-attachments-${id}`}
								className="flex flex-wrap gap-2 mt-2"
							>
								{attachments.map(attachment => (
									<button
										key={attachment.fileName}
										type="button"
										data-cy={`comment-attachment-${id}-${attachment.fileName}`}
										onClick={() =>
											onDownloadAttachment?.(
												id,
												attachment,
											)
										}
										className="inline-flex items-center gap-1 rounded-full border border-neutral_faded_20 bg-neutral_faded_10 px-2 py-1 text-12px font-medium leading-16px text-foreground_faded hover:bg-neutral_faded_20"
									>
										<Icon
											path={icon.mdiFileDocumentOutline}
											className="shrink-0 w-3 h-3"
										/>
										<span className="break-words">
											{attachment.fileName}
										</span>
									</button>
								))}
							</div>
						)}
					</>
				)}
			</div>
			<CommentDeleteModal
				isOpen={isDeleteOpen}
				isSubmitting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={() => setIsDeleteOpen(false)}
			/>
		</div>
	)
}
